import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MultiTable from "../../../Common/MUITable";
import {
  calculateCoveragePercent,
} from "./reportCommon";
import {
  DEFAULT_WARD_COVERAGE_FILTERS,
} from "./staticReportData";
import WardCoverageFilter from "./WardCoverageFilter";
import DelhiReportExport from "./DelhiReportExport";
import { GetWardCoverageReport, GetMasterCircles } from "./ReportApi";

const buildTotalRow = (rows, apiTotalVehiclesDeployed) => {
  const totals = rows.reduce(
    (acc, row) => ({
      planned: acc.planned + (row.planned || 0),
      vehicleDeployed: acc.vehicleDeployed + (row.vehicleDeployed || 0),
      totalHaltPoints: acc.totalHaltPoints + (row.totalHaltPoints || 0),
      clearedPoints: acc.clearedPoints + (row.clearedPoints || 0),
    }),
    { planned: 0, vehicleDeployed: 0, totalHaltPoints: 0, clearedPoints: 0 }
  );

  return {
    wardName: "Total",
    isTotalRow: true,
    planned: totals.planned,
    vehicleDeployed: apiTotalVehiclesDeployed !== undefined ? apiTotalVehiclesDeployed : totals.vehicleDeployed,
    totalHaltPoints: totals.totalHaltPoints,
    clearedPoints: totals.clearedPoints,
    balance: totals.totalHaltPoints - totals.clearedPoints,
    coveragePercent: calculateCoveragePercent(
      totals.totalHaltPoints,
      totals.clearedPoints
    ),
  };
};

const totalCellStyle = { fontWeight: 700 };

const renderTotalAwareCell = (field) => (row) =>
  row.isTotalRow ? (
    <span style={totalCellStyle}>{row[field]}</span>
  ) : (
    row[field]
  );

const deployedLinkStyle = {
  cursor: "pointer",
  color: "#003366",
  textDecoration: "underline",
  fontWeight: 600,
};

const exportColumns = [
  { label: "Ward", field: "wardName" },
  { label: "Planned Vehicles", field: "planned" },
  { label: "Vehicles Deployed", field: "vehicleDeployed" },
  { label: "Total No Of Halt Points", field: "totalHaltPoints" },
  { label: "Cleared Points", field: "clearedPoints" },
  { label: "Balance", field: "balance" },
  { label: "% (Total And Cleared)", field: "coveragePercent" },
];

const WardCoverageReport = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(DEFAULT_WARD_COVERAGE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(
    DEFAULT_WARD_COVERAGE_FILTERS
  );
  
  const [reportData, setReportData] = useState([]);
  const [totalVehiclesDeployed, setTotalVehiclesDeployed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wardOptions, setWardOptions] = useState([]);

  useEffect(() => {
    const fetchWards = async () => {
      try {
        const res = await GetMasterCircles();
        if (res?.data?.data) {
          const mappedOptions = res.data.data.map(circle => ({
            wardId: circle.circleId,
            wardName: circle.circleName
          }));
          setWardOptions(mappedOptions);
        }
      } catch (error) {
        console.error("Error fetching ward options:", error);
      }
    };
    fetchWards();
  }, []);

  const fetchReportData = async (filterValues) => {
    try {
      setLoading(true);
      const res = await GetWardCoverageReport(filterValues);
      if (res?.data?.data) {
        setReportData(res.data.data);
        setTotalVehiclesDeployed(res.data.totalVehiclesDeployed || 0);
      } else {
        setReportData([]);
        setTotalVehiclesDeployed(0);
      }
    } catch (error) {
      console.error("Error fetching ward coverage report:", error);
      setReportData([]);
      setTotalVehiclesDeployed(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(appliedFilters);
  }, [appliedFilters]);

  const filteredRows = useMemo(() => {
    const rows = reportData.map((row) => ({
      ...row,
      planned: row.plannedVehicles,
      vehicleDeployed: row.vehiclesDeployed,
      coveragePercent: `${row.clearedPercentage}%`,
    }));

    if (rows.length === 0) return [];
    return [...rows, buildTotalRow(rows, totalVehiclesDeployed)];
  }, [reportData, totalVehiclesDeployed]);

  const handleSearch = (values) => {
    setAppliedFilters(values);
  };

  const handleReset = () => {
    setFilters(DEFAULT_WARD_COVERAGE_FILTERS);
    setAppliedFilters(DEFAULT_WARD_COVERAGE_FILTERS);
  };

  const handleVehicleDeployedClick = (row) => {
    navigate("/vehicle-coverage-report", { state: { parentRow: row } });
  };

  const tableColumns = [
    {
      label: "Ward",
      field: "wardName",
      width: "140px",
      render: renderTotalAwareCell("wardName"),
    },
    {
      label: "Planned Vehicles",
      field: "planned",
      width: "130px",
      render: renderTotalAwareCell("planned"),
    },
    {
      label: "Vehicles Deployed",
      field: "vehicleDeployed",
      width: "140px",
      render: (row) =>
        row.isTotalRow ? (
          <span
            style={{ ...totalCellStyle, cursor: "pointer" }}
            onClick={() => handleVehicleDeployedClick(row)}
          >
            {row.vehicleDeployed}
          </span>
        ) : (
          <span
            style={deployedLinkStyle}
            onClick={() => handleVehicleDeployedClick(row)}
          >
            {row.vehicleDeployed}
          </span>
        ),
    },
    {
      label: "Total No Of Halt Points",
      field: "totalHaltPoints",
      width: "180px",
      render: renderTotalAwareCell("totalHaltPoints"),
    },
    {
      label: "Cleared Points",
      field: "clearedPoints",
      width: "140px",
      render: renderTotalAwareCell("clearedPoints"),
    },
    {
      label: "Balance",
      field: "balance",
      width: "100px",
      render: renderTotalAwareCell("balance"),
    },
    {
      label: "% (Total And Cleared)",
      field: "coveragePercent",
      width: "180px",
      render: renderTotalAwareCell("coveragePercent"),
    },
  ];

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">Ward Coverage Report</h5>
            <p className="text-muted mb-0">
             View Ward Coverage Report.
            </p>
          </div>
          <DelhiReportExport
            reportData={filteredRows}
            reportTitle="Ward Coverage"
            columns={exportColumns}
            fileName="WardCoverageReport"
          />
        </div>

        <div className="card shadow mb-3">
          <div className="card-body">
            <WardCoverageFilter
              initialValues={filters}
              wardOptions={wardOptions}
              onSearch={(values) => {
                setFilters(values);
                handleSearch(values);
              }}
              onReset={handleReset}
            />
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="top-header text-center">
              <span>Ward Coverage Report</span>
            </div>
            <div className="table-responsive">
              <MultiTable
                needSRNumber
                columns={tableColumns}
                rows={filteredRows}
                tableHeight={500}
                hasTotalRow
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WardCoverageReport;
